import React, { useState, useEffect } from 'react';
import { api, logHttpError } from '../services/api';

interface Trophy {
  id: number;
  name: string;
  description: string;
  icon_url: string | null;
  awarded_at: string | null;
  awarded_by_username: string | null;
  reason: string | null;
}

interface TrophyShelfProps {
  userId: number;
}

const TrophyShelf: React.FC<TrophyShelfProps> = ({ userId }) => {
  const [trophies, setTrophies] = useState<Trophy[]>([]);
  const [loading, setLoading] = useState(true);
  const [hoveredTrophy, setHoveredTrophy] = useState<number | null>(null);

  useEffect(() => {
    fetchTrophies();
  }, [userId]);

  const fetchTrophies = async () => {
    try {
      const res = await api.get(`/trophies/shelf/${userId}`);
      setTrophies(res.data);
    } catch (err) {
      logHttpError("Ошибка загрузки трофеев", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-3 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="relative z-20">
      {/* Полка - БЕЗ overflow-hidden чтобы тултип не обрезался */}
      <div
        className="bg-gradient-to-b from-[#2a2d35] to-[#1a1d24] rounded-t-3xl px-8 pt-10 pb-4 border-b-8 border-[#0f1115] relative"
        style={{ minHeight: '220px' }}
      >
        {/* Деревянная текстура полки */}
        <div className="absolute inset-0 opacity-5 rounded-t-3xl" style={{
          backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 60px, rgba(255,255,255,0.1) 60px, rgba(255,255,255,0.1) 61px)'
        }} />

        {/* Трофеи */}
        <div className="relative z-10 flex flex-wrap justify-center gap-10 items-end min-h-[160px]">
          {trophies.length === 0 ? (
            <div className="text-gray-600 text-base italic py-12 w-full text-center font-medium">
              Полка пуста... Пока нет наград
            </div>
          ) : (
            trophies.map((trophy) => (
              <div
                key={trophy.id}
                className="relative group cursor-pointer transform hover:-translate-y-3 transition-transform duration-300"
                onMouseEnter={() => setHoveredTrophy(trophy.id)}
                onMouseLeave={() => setHoveredTrophy(null)}
              >
                {/* Тень/силуэт трофея */}
                <div className="w-24 h-28 relative">
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-20 h-5 bg-gradient-to-t from-black/60 to-transparent rounded-full blur-md" />

                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 w-20 h-24 flex items-center justify-center">
                    {trophy.icon_url ? (
                      <img
                        src={trophy.icon_url}
                        alt={trophy.name}
                        className="w-full h-full object-contain drop-shadow-2xl"
                      />
                    ) : (
                      <div className="w-full h-full relative">
                        <div className="absolute inset-x-3 top-0 bottom-6 bg-gradient-to-b from-yellow-500/40 to-yellow-800/60 rounded-t-xl border border-yellow-600/60 backdrop-blur-sm shadow-lg" />
                        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3 h-6 bg-gradient-to-b from-yellow-600/60 to-yellow-900/80" />
                        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-10 h-3 bg-gradient-to-b from-yellow-700/70 to-black/90 rounded-b-md shadow-md" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Tooltip - ИСПРАВЛЕН: яркий контрастный дизайн */}
                {hoveredTrophy === trophy.id && (
                  <div
                    className="fixed sm:absolute bottom-full left-1/2 -translate-x-1/2 mb-6 w-80 bg-[#1a1d24] border-2 border-yellow-500/60 rounded-2xl p-6 shadow-[0_0_40px_rgba(234,179,8,0.3)] z-[100] pointer-events-none animate-fade-in"
                    style={{ position: 'absolute' }}
                  >
                    {/* Стрелка tooltip */}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 w-4 h-4 bg-[#1a1d24] border-r-2 border-b-2 border-yellow-500/60 transform rotate-45 -mt-2" />

                    <h4 className="text-yellow-400 font-black text-xl mb-2">
                      🏆 {trophy.name}
                    </h4>

                    <p className="text-white text-sm mb-4 leading-relaxed">
                      {trophy.description}
                    </p>

                    {/* ПРИЧИНА ВЫДАЧИ - МАКСИМАЛЬНО ВИДИМЫЙ БЛОК */}
                    {trophy.reason && (
                      <div className="bg-yellow-500/20 border-2 border-yellow-400/50 rounded-xl p-4 mb-4">
                        <p className="text-yellow-300 text-xs uppercase tracking-wider font-bold mb-2">
                          ⭐ Причина выдачи:
                        </p>
                        <p className="text-white text-lg font-bold leading-snug">
                          "{trophy.reason}"
                        </p>
                      </div>
                    )}

                    {!trophy.reason && (
                      <div className="bg-gray-800/50 rounded-xl p-3 mb-4">
                        <p className="text-gray-400 text-sm italic">Причина не указана</p>
                      </div>
                    )}

                    <div className="flex justify-between items-center pt-3 border-t border-gray-700">
                      <span className="text-sm text-gray-300">
                        Выдал: <span className="text-white font-bold">{trophy.awarded_by_username || 'Система'}</span>
                      </span>
                      <span className="text-sm text-gray-400 font-medium">
                        {trophy.awarded_at ? new Date(trophy.awarded_at).toLocaleDateString('ru-RU') : ''}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Нижняя часть полки (тень) */}
      <div className="h-8 bg-gradient-to-b from-[#0f1115] to-transparent rounded-b-3xl" />
    </div>
  );
};

export default TrophyShelf;
